import React from 'react'

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import {
    IconButton,
    Menu,
    MenuItem,
    Typography,
    useTheme,
} from '@material-ui/core'
import {
    MoreVert as MoreIcon,
    ExpandMore as ExpandMoreIcon,
    Settings as SettingsIcon,
    AccountTree as AccountTreeIcon,
    Extension as ExtensionIcon,
    Assignment as AssignmentIcon,
    ExitToApp as ExitToAppIcon,
} from '@material-ui/icons'

import { AppSelector, AvatarCircle, Container } from '../../../components'
import { User } from '../../../models'
import { Routes } from '../../config/routes'
import { DemoDataMenuItem } from '../../DemoDataMenuItem'
import { useHistory, useLocation } from 'react-router-dom'
import { useUser } from '../../../hooks'
import OrganizationSelection from '../TopBar/OrganizationSelection'
import SideBar from '../AuthLayout/sidebar'
import WorkSpaceSelection from '../TopBar/WorkSpaceSelection'
import { getSidebarWidth } from '../../constants'
import { SchoolListProvider } from '../../../contexts'

interface ManageLayoutProps {
    children: React.ReactNode
    user: User
}

export const ManageLayout: React.FC<ManageLayoutProps> = ({
    children,
    user,
}) => {
    const theme = useTheme()

    const history = useHistory()
    const location = useLocation()
    const isLeasingSeasonConfig = location.pathname.startsWith(
        Routes.manage.leasingSeasonConfig.root,
    )

    const { workspaceUser, organization, rootUser, actions } = useUser()

    const sidebarWidth = getSidebarWidth(
        workspaceUser?.active_workspace.company_type,
    )

    const useStyles = makeStyles<Theme, { sidebarWidth: number }>((theme) =>
        createStyles({
            root: {
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                width: '100vw',
            },

            content: {
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: 'auto',
                minHeight: 0,
                width: ({ sidebarWidth }) => `calc(100vw - ${sidebarWidth}px)`,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
            },
            topBar: {
                backgroundColor: theme.palette.primary.dark,
                minHeight: '56px',
                maxHeight: '56px',
                width: '100%',
                color: theme.palette.white.main,
                alignItems: 'center',
            },
            sectionDesktop: {
                display: 'none',
                [theme.breakpoints.up('md')]: {
                    display: 'flex',
                },
            },
            sectionMobile: {
                display: 'flex',
                [theme.breakpoints.up('md')]: {
                    display: 'none',
                },
            },
            profilePic: {
                display: 'flex',
            },
            menuItem: {
                '&:hover': {
                    backgroundColor: theme.palette.grey[300],
                },
            },
        }),
    )
    const classes = useStyles({ sidebarWidth })

    const { logout } = actions

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

    const [
        mobileMoreAnchorEl,
        setMobileMoreAnchorEl,
    ] = React.useState<null | HTMLElement>(null)

    const isMenuOpen = Boolean(anchorEl)
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        handleMobileMenuClose()
    }

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget)
    }

    const menuId = 'primary-search-account-menu'
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
            style={{ marginTop: '40px' }}
        >
            <MenuItem
                onClick={() => {
                    history.push(Routes.shared.settings.root)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <SettingsIcon style={{ marginRight: '8px' }} />
                Workspace Settings
            </MenuItem>
            <DemoDataMenuItem
                className={classes.menuItem}
                onClose={handleMenuClose}
            />
            <MenuItem
                onClick={() => {
                    handleMenuClose()
                    history.push(Routes.manage.leasingSeasonConfig.root)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <AccountTreeIcon style={{ marginRight: '8px' }} />
                Leasing Season Config
            </MenuItem>
            <MenuItem
                onClick={() => {
                    handleMenuClose()
                    history.push(Routes.manage.integrations)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <ExtensionIcon style={{ marginRight: '8px' }} />
                Integrations
            </MenuItem>
            <MenuItem
                onClick={() => {
                    history.push(Routes.shared.reports)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <AssignmentIcon style={{ marginRight: '8px' }} />
                Reports
            </MenuItem>
            <MenuItem
                onClick={() => {
                    logout()
                    history.push(Routes.public.login)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <ExitToAppIcon style={{ marginRight: '8px' }} />
                Logout
            </MenuItem>
        </Menu>
    )

    const mobileMenuId = 'primary-search-account-menu-mobile'
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem onClick={handleProfileMenuOpen}>
                <IconButton
                    aria-label="account of current user"
                    aria-controls="primary-search-account-menu"
                    aria-haspopup="true"
                    color="inherit"
                >
                    <AvatarCircle
                        text={rootUser?.name}
                        initials
                        image={rootUser?.profile_picture}
                        styles={{
                            container: {
                                maxHeight: '30px',
                                maxWidth: '30px',
                                minHeight: 'auto',
                                minWidth: 'auto',
                                fontSize: '1rem',
                                padding: '16px',
                            },
                        }}
                    />
                    {/* <AccountCircle /> */}
                </IconButton>
                <p>Profile</p>
            </MenuItem>
            <DemoDataMenuItem onClose={handleMobileMenuClose} />
            <MenuItem
                onClick={() => {
                    handleMobileMenuClose()
                    history.push(Routes.manage.leasingSeasonConfig.root)
                }}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <AccountTreeIcon style={{ marginRight: '8px' }} />
                Leasing Season Config
            </MenuItem>
            <MenuItem
                onClick={() => {
                    handleMobileMenuClose()
                    history.push(Routes.manage.integrations)
                }}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <ExtensionIcon style={{ marginRight: '8px' }} />
                Integrations
            </MenuItem>
        </Menu>
    )

    return (
        <SchoolListProvider>
            <Container style={{ height: '100vh', width: '100vw' }}>
                {/* Left Side Bar */}
                {/* <OrganizationSideBar open drawerWidth={DRAWER_WIDTH} /> */}

                <SideBar
                    open={true}
                    drawerWidth={sidebarWidth}
                    selectedChannel="none"
                    setSelectedChannel={() => {}}
                />

                {/* Right Panel, top bar and content */}
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Container
                        className={classes.topBar}
                        style={{ padding: theme.spacing(2) }}
                    >
                        {/* <OrganizationSelection
                        user={user}
                        selectedOrganization={organization}
                    /> */}
                        {rootUser?.workspaces.length !== 0 && (
                            <WorkSpaceSelection />
                        )}

                        <Container style={{ flex: 1 }} />

                        <AppSelector
                            workspaceUser={workspaceUser}
                            theme={theme}
                        />

                        <div className={classes.sectionDesktop}>
                            <IconButton
                                edge="end"
                                aria-label="account of current user"
                                aria-controls={menuId}
                                aria-haspopup="true"
                                onClick={handleProfileMenuOpen}
                                color="inherit"
                            >
                                <Typography style={{ marginRight: '5px' }}>
                                    {rootUser?.name}
                                </Typography>
                                <AvatarCircle
                                    text={rootUser?.name}
                                    initials
                                    image={rootUser?.profile_picture}
                                    styles={{
                                        container: {
                                            maxHeight: 45,
                                            maxWidth: 45,
                                            minHeight: 'auto',
                                            minWidth: 'auto',
                                            fontSize: '1rem',
                                        },
                                    }}
                                />
                                <ExpandMoreIcon style={{ marginLeft: '5px' }} />
                            </IconButton>
                        </div>
                        <div className={classes.sectionMobile}>
                            <IconButton
                                aria-label="show more"
                                aria-controls={mobileMenuId}
                                aria-haspopup="true"
                                onClick={handleMobileMenuOpen}
                                color="inherit"
                            >
                                <MoreIcon />
                            </IconButton>
                        </div>
                    </Container>

                    {renderMobileMenu}
                    {renderMenu}

                    {/* Content */}
                    <main
                        className={classes.content}
                        style={{
                            padding: isLeasingSeasonConfig ? '8px' : '24px',
                        }}
                    >
                        {children}
                    </main>
                </Container>
            </Container>
        </SchoolListProvider>
    )
}
